import React, { useEffect, useState, useRef, useCallback, forwardRef, SetStateAction } from 'react'
import { tv, type VariantProps } from 'tailwind-variants'
import IconUse from '_/ui/icons/use'
import ScrollableArea from '_/ui/scrollable-area'
import { Checkbox } from './checkbox'
import { Label } from './label'

export const input = tv({
	base: 'text-base font-normal rounded border px-6 py-3 pr-20 w-full focus:ring-2 focus:ring-primary-neutral cursor-pointer group',
	variants: {
		themeColor: {
			default: 'text-gray-200 border-primary-light hover:border-gray-700',
			invalid: 'text-red-500 border-red-500',
			success: 'text-primary-neutral border-primary-neutral',
		},
	},
	defaultVariants: {
		themeColor: 'default',
	},
})

export const wrapper = tv({
	base: 'relative w-full items-center',
	variants: {
		blocked: {
			true: 'opacity-50 pointer-events-none',
		},
	},
})

export const dropdown = tv({
	base: 'absolute top-full w-full px-3 py-4 left-0 mt-2 bg-gray-100 border border-primary-light rounded-md shadow-sm z-50 max-h-36 overflow-auto',
})

export const optionItem = tv({
	base: 'relative gap-2 cursor-pointer items-center mb-3 font-bold block w-full',
})

export const dropdownIcon = tv({
	base: 'absolute right-3 top-3 pointer-events-none ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300 data-[state=true]:rotate-180',
})

interface InputProps extends VariantProps<typeof input>, React.InputHTMLAttributes<HTMLInputElement> {
	className?: string
	id?: string
	label?: string
	multiple?: boolean
	name: string
	onInputChange?: (inputName: string, newValue: any, valueId?: string) => void
	options?: { value: string; label: string }[]
	placeholder?: string
	blocked?: boolean
	value?: string[]
	setFieldValue?: (node: string | number, value: string, valueId?: string | undefined) => void
	handleClearValue?: () => void
}

const SelectComponent = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			className,
			id,
			label,
			multiple,
			name,
			onInputChange,
			options,
			placeholder,
			themeColor,
			blocked,
			value = [],
			setFieldValue,
			handleClearValue,
			...rest
		},
		ref,
	) => {
		const [selectedValues, setSelectedValues] = useState<string[]>(Array.isArray(value) ? value : [])

		const [isDropdownOpen, setIsDropdownOpen] = useState(false)
		const dropdownRef = useRef(null)

		const handleInputChange = useCallback(
			(values: string[]) => {
				if (onInputChange) {
					onInputChange(name, values)
				}
			},
			[name, onInputChange],
		)

		const toggleValue = (currentValue: string, idealValue?: string) => {
			let tempValues: SetStateAction<string[]>
			if (multiple) {
				if (selectedValues.includes(currentValue)) {
					tempValues = selectedValues.filter(v => v !== currentValue)
				} else {
					tempValues = [...selectedValues, currentValue]
				}
			} else {
				if (selectedValues.includes(currentValue)) {
					tempValues = []
				} else {
					tempValues = [currentValue]
				}
			}

			setSelectedValues(tempValues)
			if (onInputChange) {
				onInputChange(name, tempValues)
			}

			if (setFieldValue && idealValue) {
				setFieldValue(name, [tempValues][0] as unknown as string, idealValue)
			}

			closeDropdown()
		}

		const toggleDropdown = () => setIsDropdownOpen(state => !state)

		const closeDropdown = useCallback(
			(check = false) => {
				setIsDropdownOpen(false)
				if (check) {
					handleInputChange(selectedValues)
				}
			},
			[handleInputChange, selectedValues],
		)

		const clearValues = () => {
			setSelectedValues([])
			if (onInputChange) {
				onInputChange(name, [])
			}
		}

		useEffect(() => {
			function handleClickOutside(event: MouseEvent) {
				if (
					dropdownRef.current &&
					!(dropdownRef.current as any).contains(event.target) &&
					(event.target as HTMLElement).nodeName.toLowerCase() !== 'input'
				) {
					closeDropdown(true)
				}
			}

			document.addEventListener('mousedown', handleClickOutside)
			return () => {
				document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [closeDropdown])

		useEffect(() => {
			if (!value) {
				setSelectedValues([])
			} else {
				setSelectedValues(Array.isArray(value) ? value : [value])
			}
		}, [value])

		return (
			<>
				{label && <Label htmlFor={name}>{label}</Label>}
				<div className={wrapper({ class: className, blocked })}>
					<input
						{...rest}
						id={id ? id : name}
						name={name}
						value={selectedValues}
						onClick={toggleDropdown}
						onChange={() => handleInputChange(selectedValues)}
						className={input({ class: className, themeColor })}
						placeholder={placeholder && placeholder}
						readOnly
						ref={ref}
					/>
					<IconUse
						id='chevron-down'
						width={24}
						height={24}
						data-state={isDropdownOpen}
						className={dropdownIcon({ class: className })}
					/>
					{selectedValues && selectedValues.length > 0 && (
						<IconUse
							id='close'
							width={15}
							height={15}
							className='absolute text-primary-neutral right-12 top-4 cursor-pointer'
							onClick={() => {
								clearValues()
								closeDropdown()
								if (handleClearValue) {
									handleClearValue()
								}
							}}
						/>
					)}
					{isDropdownOpen && (
						<div className={dropdown()} ref={dropdownRef}>
							<ScrollableArea className='h-full'>
								{options &&
									options.map((option, index) => (
										<div
											key={index}
											onClick={() => toggleValue(option.label, option.value)}
											className={`${
												selectedValues && selectedValues.includes(option.label) ? 'text-primary-neutral' : ''
											} ${optionItem({ class: className })}`}
										>
											<Checkbox
												hasCheckbox={false}
												name={option.value}
												label={option.label}
												className='font-bold pointer-events-none text-inherit'
												defaultValue={selectedValues && selectedValues.includes(option.label)}
												checked={selectedValues && selectedValues.includes(option.label)}
											/>
										</div>
									))}
							</ScrollableArea>
						</div>
					)}
				</div>
			</>
		)
	},
)

SelectComponent.displayName = 'SelectComponent'

export const Select = SelectComponent
